import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AtlasData } from '../types';
import { latest } from '../helpers';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function EmployerSatisfactionCard({ data }: { data: AtlasData }) {
  const theme = useMantineTheme();
  const series = data.employerSatisfaction;
  const cur = series.length > 0 ? latest(series) : null;
  const goal = 80;

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const ytdAvg =
    currentMeta &&
    (() => {
      const points = series.filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
      });
      if (points.length === 0) return null;
      const sum = points.reduce((acc, p) => acc + p.percentSatisfied, 0);
      return sum / points.length;
    })();

  const yoyAvg =
    currentMeta &&
    (() => {
      const prevPoints = series.filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      });
      if (prevPoints.length === 0 || ytdAvg === null) return null;
      const sum = prevPoints.reduce((acc, p) => acc + p.percentSatisfied, 0);
      return ytdAvg - sum / prevPoints.length;
    })();

  const yoyText =
    yoyAvg === null
      ? '— YoY'
      : yoyAvg === 0
        ? '0.00% YoY'
        : `${yoyAvg > 0 ? '↑' : '↓'}${Math.abs(yoyAvg).toFixed(2)}% YoY`;

  const allTimeAvg = series.length
    ? series.reduce((acc, p) => acc + p.percentSatisfied, 0) / series.length
    : null;

  const status = getMultiGoalStatus([
    getThresholdGoalState({ value: cur?.percentSatisfied ?? 0, goal, direction: 'min' }),
    ...(ytdAvg !== null ? [getThresholdGoalState({ value: ytdAvg, goal, direction: 'min' })] : []),
    ...(allTimeAvg !== null ? [getThresholdGoalState({ value: allTimeAvg, goal, direction: 'min' })] : []),
  ]);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell
      title="Employer Satisfaction (Graduate Readiness)"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Percent of employers satisfied with graduate readiness this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur ? `${Math.round(cur.percentSatisfied)}%` : '—'}
                </Text>
              ),
              sublabel: `Goal ${goal}%`,
            },
            {
              label: 'YTD',
              labelTooltip: 'Year-to-date average satisfaction (across quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytdAvg === null ? '—' : `${Math.round(ytdAvg)}%`}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'All-Time',
              labelTooltip: 'Average satisfaction across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {allTimeAvg === null ? '—' : `${Math.round(allTimeAvg)}%`}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Quarterly Satisfaction Trend
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis domain={[0, 100]} orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} width={44} tickMargin={8} />
              <ReferenceLine y={goal} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const v = payload[0]?.value;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                        {typeof v === 'number' ? Math.round(v) : '—'}%
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="percentSatisfied"
                name="Satisfaction"
                fill={theme.colors.blue[6]}
                radius={[6, 6, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
